// Note markers — the placement of a «Заметка» inside scene Fountain text — and
// note records, the content behind them.
//
// The persisted scene text carries only a one-line placement marker:
//
//     [[note:<uuid>]]
//
// Everything else about the note — body, quote, threading, authorship — is a
// `NoteRecord`, persisted per note as a `scene_note` text atom (see
// `sourse/data/notesStore.ts`). The marker's uuid is the join key: the engine
// resolves a marker to its record at hydration, and the record's server-side
// atom id never appears in scene text — so markers are never rewritten when
// ids are assigned.
//
// Keeping the marker in the text (rather than a positional sidecar) keeps ONE
// positional coordinate space: `paragraph_lines` and `draft_line_ids` index the
// same line array the markers live in.
//
// Two non-bare marker shapes still PARSE, because ingestion must adopt them:
//   - **legacy** — a plain Fountain `[[free text]]` note, single- or
//     multi-line, from an imported script or typed by hand. It has no id;
//     `normalizeNoteMarkersInContent` mints one on ingestion.
//   - **full payload** — a transitional dev format (`[[note:<uuid> parent:…
//     by:…` + quote/body lines), read so dev-DB scenes saved in it still open
//     with their notes intact. Never written.

/** One note's content and metadata — what the marker's uuid resolves to. */
export interface NoteRecord {
    /** Client-minted UUID, the marker join key. `''` only for a legacy marker
     *  that has not been adopted yet (an ephemeral, parse-time record). */
    id: string;
    /** The note this one replies to, or `null` for a standalone note. */
    parentId: string | null;
    /** Label of the user who created the note. `null` on legacy/unstamped notes. */
    author: string | null;
    /** Id of the user who created the note — the ownership key (#1836): only
     *  this user may edit or delete it, and the server stamps it itself
     *  (`content_json.by.user_id`). Absent or `null` on a legacy note written
     *  before ownership existed, which any author or editor may touch. */
    authorId?: number | null;
    createdAt: string | null;
    /** Label of the user who last edited the note (may equal `author`). */
    updatedBy: string | null;
    updatedById?: number | null;
    updatedAt: string | null;
    /** The whole citation, ONE editable email-style string: each
     *  quoted message's attribution line — «20.07.2026, 14:32, ivan
     *  написал(а):» — sits `>`-marked directly above its `>`-marked body, at
     *  the same depth, oldest message on top and the one being answered at the
     *  bottom. The markers are part of the field's text, not CSS decoration —
     *  they survive selection and copy, and everything (dates included) is
     *  plain editable text. `''` when the note quotes nothing. Wire name:
     *  `content_json.quote_text`; the retired separate attribution
     *  (`content_json.quote_head`) is folded into this on read — see
     *  {@link normalizeNoteRecord}. */
    quote: string;
    /** The note body. `''` when empty. */
    text: string;
}

/** Inclusive line-index range of one marker within a scene's raw lines. */
export interface NoteSpan {
    start: number;
    end: number;
}

/**
 * Who touched a note, and when — the authorship snapshot taken at the moment a
 * note is created or its body is edited. `user` is a display label (name,
 * falling back to email); `''` means "unknown", which stamps the note
 * authorless rather than inventing an empty name.
 */
export interface NoteStamp {
    user: string;
    at: string;
    /** The authenticated user's id; absent when the host has none (tests,
     *  fixtures) — the note then stamps ownerless, exactly like a legacy one. */
    userId?: number | null;
}

/**
 * A line-count change ingestion made to a scene's content: every line index
 * recorded BEFORE the rewrite that is at or after `fromIndex` now sits
 * `delta` lines lower (negative `delta` = higher).
 *
 * This exists because `paragraph_lines[].index` and `draft_line_ids` are
 * POSITIONAL on the wire. Normalization replaces a multi-line legacy or
 * full-payload marker with a single bare line, so without this every tag and
 * draft flag below such a note would silently re-attach to the wrong line.
 */
export interface LineIndexShift {
    fromIndex: number;
    delta: number;
}

/** Result of {@link normalizeNoteMarkersInContent}. */
export interface NoteNormalization {
    content: string;
    shifts: LineIndexShift[];
    /** The records extracted from the normalized markers, in document order —
     *  the caller owns storing them (engine state + the notes store). */
    records: NoteRecord[];
}

const OPEN = '[[';
const CLOSE = ']]';
const FULL_PAYLOAD_OPEN = '[[note:';
/** The named header fields of the transitional full-payload form. */
const HEADER_KEYS = ['note', 'parent', 'quote', 'by', 'edited'] as const;
const HEADER_KEY_RE = /(?:^|\s)(note|parent|quote|by|edited):/g;
/** A bare placement marker: `[[note:<uuid>]]`, nothing else on the line. */
const BARE_MARKER_RE = /^\s*\[\[note:([^\s\]]+)\]\]\s*$/;

/** The canonical wire form of a note's placement. */
export function bareNoteMarker(id: string): string {
    return `${FULL_PAYLOAD_OPEN}${id}${CLOSE}`;
}

/** The uuid of a bare placement marker, or `null` when `line` is anything
 *  else (legacy note, full-payload marker, ordinary text). */
export function parseBareMarkerId(line: string): string | null {
    const match = BARE_MARKER_RE.exec(line);
    return match?.[1] ?? null;
}

/**
 * Locate every note marker in a scene's raw lines. A marker opens on a line
 * whose first non-space characters are `[[` and closes on the first line at or
 * after it that ends with `]]`. An opener with no closing line is not a marker
 * (matching Fountain).
 *
 * How far an opener may reach depends on which form it is, and that asymmetry
 * is load-bearing. A **full-payload** `[[note:` opener is machine-written and
 * well-formed by construction, so it reaches the first `]]` however far away —
 * a note body may contain blank lines. A **bare** `[[` opener may be a person
 * mid-typing, and every line it swallows is hidden while it is unclosed: with
 * an existing note anywhere below, typing `[[` would make the whole span
 * between them vanish. So a bare opener stops at the first blank line, which is
 * also what a plain Fountain note is — a paragraph-level construct.
 */
export function findNoteSpans(lines: readonly string[]): NoteSpan[] {
    const spans: NoteSpan[] = [];
    for (let i = 0; i < lines.length; i++) {
        const opener = (lines[i] ?? '').trimStart();
        if (!opener.startsWith(OPEN)) continue;
        const paragraphBound = !opener.startsWith(FULL_PAYLOAD_OPEN);
        let end = -1;
        for (let j = i; j < lines.length; j++) {
            const line = lines[j] ?? '';
            if (line.trimEnd().endsWith(CLOSE)) {
                end = j;
                break;
            }
            if (paragraphBound && j > i && line.trim() === '') break;
        }
        if (end < 0) continue;
        spans.push({ start: i, end });
        i = end;
    }
    return spans;
}

/**
 * Expand spans into a per-line lookup: `result[i]` is the start index of the
 * marker line `i` belongs to, or `-1` when it belongs to none. Built on
 * {@link findNoteSpans}, like the parser's own lookup, so every walk that claims
 * a marker's lines wholesale — the parser, the engine's flat-line walk and the
 * visibility detector — agrees about where a note begins and ends.
 */
export function markerStartByLine(lines: readonly string[]): number[] {
    const starts = new Array<number>(lines.length).fill(-1);
    for (const span of findNoteSpans(lines)) {
        for (let i = span.start; i <= span.end; i++) starts[i] = span.start;
    }
    return starts;
}

/**
 * Parse one marker's raw text (the joined lines of a `NoteSpan`) into the
 * record it carries. Ingestion-time only: a bare marker yields a record with
 * just its `id` (the content lives elsewhere), a legacy `[[free text]]` yields
 * an id-less record whose body is the text, and the transitional full-payload
 * form yields everything it carried. Returns `null` when `raw` is not a marker
 * at all.
 */
export function parseNoteMarker(raw: string): NoteRecord | null {
    const trimmed = raw.trim();
    if (!trimmed.startsWith(OPEN) || !trimmed.endsWith(CLOSE)) return null;

    const lines = raw.split('\n');
    const last = lines.length - 1;
    // Drop the terminator from the closing line; everything else stays verbatim.
    lines[last] = (lines[last] ?? '').trimEnd().slice(0, -CLOSE.length);
    const head = lines[0] ?? '';

    if (!head.trimStart().startsWith(FULL_PAYLOAD_OPEN)) {
        // Legacy `[[free text]]` — the whole marker is body.
        lines[0] = head.trimStart().slice(OPEN.length);
        return { ...emptyNote(), text: lines.join('\n').trim() };
    }

    const fields = parseHeaderFields(head.trimStart().slice(OPEN.length));
    const by = splitStamp(fields.by);
    const edited = splitStamp(fields.edited);
    const parent = fields.parent ?? null;
    // An explicit `quote:<n>` pins the boundary; without it the quote is the
    // leading run of `>` lines (the transitional format's disambiguator for a
    // body that itself starts with `>`).
    const declared = fields.quote === undefined ? null : Number.parseInt(fields.quote, 10);
    const quote: string[] = [];
    let bodyStart = 1;
    if (declared !== null && Number.isFinite(declared)) {
        const until = Math.min(last, declared);
        while (bodyStart <= until) {
            quote.push(lines[bodyStart] ?? '');
            bodyStart++;
        }
    } else {
        while (bodyStart <= last && (lines[bodyStart] ?? '').startsWith('>')) {
            quote.push(lines[bodyStart] ?? '');
            bodyStart++;
        }
    }
    return {
        id: fields.note ?? '',
        parentId: parent === null || parent === '-' ? null : parent,
        author: by.user,
        createdAt: by.iso,
        updatedBy: edited.user,
        updatedAt: edited.iso,
        quote: quote.join('\n'),
        text: lines.slice(bodyStart).join('\n'),
    };
}

/** True for a marker that has never been adopted — a plain Fountain
 *  `[[free text]]` note with no id of its own. */
export function isLegacyNote(note: NoteRecord): boolean {
    return note.id === '';
}

/**
 * A brand-new note that answers to nobody: no parent, no quote, freshly
 * stamped, carrying `text` as its body. Every conversion INTO a note produces
 * one of these — a note only ever becomes a reply by being created as one
 * («Ответить» / ⌘⇧M), never by a block changing type.
 */
export function makeStandaloneNote(id: string, text: string, stamp: NoteStamp): NoteRecord {
    const user = stamp.user === '' ? null : stamp.user;
    const userId = stamp.userId ?? null;
    return {
        id,
        parentId: null,
        author: user,
        authorId: userId,
        createdAt: user === null ? null : stamp.at,
        updatedBy: user,
        updatedById: userId,
        updatedAt: stamp.at,
        quote: '',
        text,
    };
}

/**
 * Record who last changed a note and when. Authorship (`author`/`createdAt`)
 * is untouched — any member may edit any note, and the card distinguishes the
 * two («изменено: X» only when the editor isn't the author).
 */
export function stampNoteEdit(note: NoteRecord, stamp: NoteStamp): NoteRecord {
    return { ...note, updatedBy: stamp.user === '' ? null : stamp.user, updatedById: stamp.userId ?? null, updatedAt: stamp.at };
}

/**
 * Whether the note has nothing to say. **The quote does not count**: a note
 * that only carries the fragment it was going to answer is an unfinished note,
 * so it reads as empty everywhere the editor asks "does this block have
 * content" — the placeholder shows, and [×] deletes it without a confirmation.
 */
export function isNoteEmpty(note: NoteRecord): boolean {
    return note.text.trim() === '';
}

/** Field-wise equality — an unchanged note surviving a scene re-parse comes
 *  back as a fresh object, and renderers must not treat that as a change. */
export function noteRecordsEqual(a: NoteRecord | null, b: NoteRecord | null): boolean {
    if (a === b) return true;
    if (a === null || b === null) return false;
    return (
        a.id === b.id &&
        a.parentId === b.parentId &&
        a.author === b.author &&
        a.createdAt === b.createdAt &&
        a.updatedBy === b.updatedBy &&
        a.updatedAt === b.updatedAt &&
        a.quote === b.quote &&
        a.text === b.text
    );
}

/** A quote chain cut in two: what this reply directly answers, and the older
 *  history it inherited from the note it answered. Either half may be `''`. */
export interface QuoteChainSplit {
    /** The message being answered — the trailing depth-1 run at the BOTTOM of
     *  the citation (its attribution, then its words). Always shown. */
    immediate: string;
    /** Everything older: the leading run of depth-≥2 lines stacked ON TOP of
     *  the citation (a citation reads oldest-first). Shown by default, folded
     *  per note by the reader; `''` for a fragment quote, which then gets no
     *  toggle. */
    inherited: string;
}

/** How many quote levels deep a line sits — `> x` is 1, `>> x` is 2, a line
 *  with no marker at all is 0. */
export function quoteLineDepth(line: string): number {
    let depth = 0;
    while (line[depth] === '>') depth++;
    return depth;
}

/**
 * Cut a citation into the message being answered and the inherited history
 * above it — the mail idiom's "trimmed" boundary, which is what the card's
 * collapse toggle hides.
 *
 * A citation built by `buildQuoteText` reads oldest-first:
 *
 *     >> 18.07.2026, masha написал(а):   ← depth ≥2: inherited history, on top
 *     >> the oldest remark
 *     > 19.07.2026, fl1po написал(а):    ← depth 1: the message being
 *     > the parent's own words           ←   answered, at the bottom
 *
 * **The boundary is read off the nesting, not off a stored index**, because the
 * quote is editable: any offset frozen at reply time would go stale the moment
 * someone trims a line. The inherited history is the LEADING run of depth-≥2
 * lines; the cut falls where that run ends. No deep run on top — a fragment
 * quote, or a hand-edited citation whose deep lines no longer lead — means
 * nothing folds, which is the graceful side of an editable field: erring open
 * can hide nobody's words behind the toggle.
 */
export function splitQuoteChain(quote: string): QuoteChainSplit {
    if (quote === '') return { immediate: '', inherited: '' };
    const lines = quote.split('\n');
    let cut = 0;
    while (cut < lines.length && quoteLineDepth(lines[cut] ?? '') >= 2) cut++;
    if (cut === 0 || cut === lines.length) return { immediate: quote, inherited: '' };
    return { immediate: lines.slice(cut).join('\n'), inherited: lines.slice(0, cut).join('\n') };
}

/* --- Keeping a citation's `>` depth and order consistent -------------------
 *
 * A citation is one editable string, so hand edits and records written by
 * older clients can drift from the canonical shape (attribution and body at
 * matching depths, oldest message on top). The three functions below realign
 * WITHOUT assuming the shape: each is idempotent and conservative, returning
 * unfamiliar text unchanged, so they are safe to run on every record crossing
 * the storage boundary (`normalizeNoteRecord`). Concept parity:
 * `normalizeQuoteMarkers` / `reverseCitationOrder` in editor.html. */

/** The text of a quoted line with its leading `>` markers (and the single
 *  space `quoteLine` puts after them) stripped off. `''` for a bare marker. */
export function stripQuoteMarkers(line: string): string {
    return line.replace(/^\s*(?:>+)\s?/, '');
}

/** Re-apply `depth` markers to `content`, matching `quoteLine`'s format: N
 *  contiguous markers, one space before non-empty text, bare markers for an
 *  empty line, the raw content at depth 0. */
export function setQuoteDepth(content: string, depth: number): string {
    const clamped = Math.max(0, depth);
    if (clamped === 0) return content;
    return content.length > 0 ? `${'>'.repeat(clamped)} ${content}` : '>'.repeat(clamped);
}

/**
 * A citation ATTRIBUTION line — «…написал(а):» (answering a note) or «Цитата
 * из блока «…»:» (citing a block) — tested on the marker-stripped content.
 * Both product languages are recognized because the line is STORED text,
 * written in whatever language its author was working in.
 *
 * The patterns mirror the attribution WRITERS: `quote-wrote` /
 * `quote-from-block` in `sourse/utils/languages/{en,ru}.json` (the host's
 * i18n wiring of the `formatNoteQuoteHead` / `formatNoteBlockQuoteHead`
 * ports) and the ports' Russian defaults in `noteQuote.ts`. A new locale for
 * those keys must extend this recognizer too, or its citations stop
 * realigning and reordering on read.
 */
export function isQuoteAttribution(content: string): boolean {
    return /(?:написал\(а\)|wrote):\s*$/.test(content) || /^(?:Цитата из блока|Quoted from)\s/.test(content);
}

/**
 * Realign a citation so every quoted message reads at one consistent depth —
 * the newest message (the one being answered) at 1, the message IT quotes at
 * 2, and so on — with each message's attribution carrying the SAME marker
 * count as its own body. The body sits directly beneath its attribution, so
 * the attribution simply borrows that next line's depth. Fixes the legacy
 * drift (a top attribution with no `>` at all, nested ones off by one);
 * already-consistent text passes through unchanged.
 */
export function normalizeQuoteMarkers(quote: string): string {
    const lines = quote.split('\n');
    for (let i = 0; i < lines.length; i++) {
        const content = stripQuoteMarkers(lines[i] ?? '');
        if (!isQuoteAttribution(content)) continue;
        const bodyDepth = i + 1 < lines.length ? quoteLineDepth(lines[i + 1] ?? '') : 0;
        const depth = bodyDepth > 0 ? bodyDepth : quoteLineDepth(lines[i] ?? '') || 1;
        lines[i] = setQuoteDepth(content, depth);
    }
    return lines.join('\n');
}

/**
 * Reorder a stored citation from the legacy "newest on top" layout to the
 * current "oldest on top, answered message at the bottom" one.
 * Splits the citation into messages (each led by an attribution line) and,
 * when they run shallow→deep top-to-bottom (the old order), reverses them —
 * dropping the blank `>` spacer lines the old writer put between messages.
 * Idempotent and conservative: a citation already in the new order, a
 * single-message one, or an unfamiliar hand-edited shape comes back unchanged.
 */
export function reverseCitationOrder(quote: string): string {
    const groups: string[][] = [];
    for (const line of quote.split('\n')) {
        const content = stripQuoteMarkers(line);
        if (content.trim() === '') continue; // drop blank `>` spacer lines
        const lastGroup = groups[groups.length - 1];
        if (isQuoteAttribution(content)) groups.push([line]);
        else if (lastGroup !== undefined) lastGroup.push(line);
        else return quote; // body before any attribution — unfamiliar shape
    }
    if (groups.length < 2) return quote;
    const depthOf = (group: string[]) => quoteLineDepth(group[0] ?? '');
    const first = groups[0];
    const last = groups[groups.length - 1];
    if (first === undefined || last === undefined) return quote;
    // Top message already the deepest → already the new order (or flat).
    if (depthOf(first) >= depthOf(last)) return quote;
    return groups.reverse().flat().join('\n');
}

/**
 * What the quote FIELD holds: the fragment alone while the inherited history is
 * collapsed, the whole chain once it is expanded.
 *
 * One function rather than a ternary at each site, because the sites are the
 * cards' render and the pagination height model — and a note whose measured
 * rows disagree with its estimated ones moves page breaks when it scrolls in.
 */
export function visibleQuoteText(quote: string, expanded: boolean): string {
    if (expanded || quote === '') return quote;
    return splitQuoteChain(quote).immediate;
}

/**
 * Put a split chain back together — the commit side of the collapse. What the
 * user typed replaces the answered message at the bottom; the history is
 * re-attached verbatim ON TOP (oldest-first), because while it is collapsed
 * they never saw it and cannot have meant to change it.
 */
export function joinQuoteChain(immediate: string, inherited: string): string {
    if (inherited === '') return immediate;
    // Emptying the visible half must not leave the chain trailing off on a
    // blank line — the quote then simply IS its history.
    if (immediate === '') return inherited;
    return `${inherited}\n${immediate}`;
}

/**
 * The rows a note card occupies once rendered: its metadata head (when it has
 * any stamps), then the quote's lines, then the body's — with the placeholder
 * standing in for an empty body. Mirrors what `NoteCard.tsx` draws, and exists
 * so the pagination height model can estimate a note that has never been
 * measured (the marker's raw line is join syntax, not a rendered row, and
 * estimating from it makes a six-line note paginate as one).
 */
export function noteCardLines(note: NoteRecord, placeholder: string, options: { expandedQuote?: boolean } = {}): string[] {
    const rows: string[] = [];
    if (note.author !== null || note.updatedAt !== null || note.createdAt !== null) rows.push('head');
    if (note.quote !== '') {
        // The inherited history SHOWS by default, so it counts toward the
        // card's height unless the reader has folded this note —
        // and the toggle that folds it takes a row of its own, on any note
        // that has one. A fragment quote has neither.
        const shown = visibleQuoteText(note.quote, options.expandedQuote !== false);
        if (shown !== '') rows.push(...shown.split('\n'));
        if (splitQuoteChain(note.quote).inherited !== '') rows.push('quote-toggle');
    }
    rows.push(...(note.text === '' ? [placeholder] : note.text.split('\n')));
    // «Ответить» is invisible at rest but still LAID OUT (see NoteCard.css), so
    // it occupies a row whether or not anyone is hovering. Counted here for the
    // same reason every other row is: a virtualized-out note that under-measures
    // paginates short and moves the page breaks when it scrolls in. (A read-only
    // editor renders no button and is over-estimated by this one row — the
    // height model has no way to know, and one row is well inside what an
    // unmeasured estimate is worth.)
    rows.push('reply');
    return rows;
}

/**
 * The rows a note SHOWS, minus its metadata head: the quote's lines, then the
 * body's, with blank rows dropped. What a reader of the note actually reads —
 * used by the clipboard's human-facing flavors, where the note has to arrive as
 * prose rather than as a marker naming an atom the target app can't resolve.
 *
 * Blank rows go because the plain-text flavor wraps these in a bare `[[…]]`
 * note, whose opener stops at the first blank line: a body with a paragraph
 * break in it would otherwise write a marker that never closes.
 *
 * Near-twin of {@link noteCardLines}, deliberately not folded into it: that one
 * answers "how tall does this card render", so it KEEPS blank rows (they occupy
 * space) and substitutes the placeholder for an empty body. This one answers
 * "what does the note say".
 */
export function noteVisibleLines(note: NoteRecord): string[] {
    const rows = [...(note.quote === '' ? [] : note.quote.split('\n')), ...(note.text === '' ? [] : note.text.split('\n'))];
    return rows.filter(row => row.trim() !== '');
}

/**
 * The record a marker renders from, given the document's records by uuid. A
 * bare marker is a pointer — its words live in `records` — while a legacy or
 * full-payload marker carries its own content and needs no lookup. `null` for
 * an orphan: a bare marker whose record this document has never seen.
 */
export function resolveNoteForMarker(raw: string, records: ReadonlyMap<string, NoteRecord>): NoteRecord | null {
    const parsed = parseNoteMarker(raw);
    if (parsed === null) return null;
    if (parsed.id === '') return parsed;
    return records.get(parsed.id) ?? (isBareMarker(raw) ? null : parsed);
}

/**
 * A record as it arrives from STORAGE rather than from the engine: every field
 * optional, because the client version that wrote it knew a different set —
 * including `quoteHead`, the retired separate attribution line older clients
 * stored beside the quote (wire `content_json.quote_head`). `NoteRecord` is
 * assignable to it, so callers holding a well-typed record pass one straight
 * through.
 */
export type StoredNoteFields = Partial<NoteRecord> & {
    /** Legacy separate attribution — folded into `quote` on read. */
    quoteHead?: string;
};

/**
 * Fill in the fields a record arriving from OUTSIDE the engine may be missing,
 * and bring its citation to the canonical shape.
 *
 * Records cross that boundary as loose JSON — the `scene_note` atoms'
 * `content_json` — and a record written by an older client has only the
 * fields that client knew, in the shapes it wrote. Two problems
 * are settled here, once, for every door records come in through (`hydrate`,
 * `upsertNoteRecords`, the API read):
 *
 *   - `undefined` where a string is declared is not cosmetic: `noteCardLines`
 *     would put it in the pagination height model and the measurement would
 *     throw, taking the whole document's render with it;
 *   - a LEGACY citation — a separate `quoteHead` attribution, blank `>`
 *     spacers, newest-message-first order, attributions one depth shallower
 *     than their bodies — is folded into the one editable string the card now
 *     renders: head merged in as a quoted line, every attribution realigned
 *     to its body's depth, messages reordered oldest-first. Each step is
 *     idempotent, so an already-canonical record converges to itself.
 *
 * Identity is preserved for an already-canonical record ON PURPOSE: the
 * factory's note flush diffs by reference against what it last wrote, so
 * handing back a fresh object for every record the store just loaded would
 * echo the whole store straight back at it on hydrate.
 */
export function normalizeNoteRecord(record: StoredNoteFields): NoteRecord {
    const storedQuote = record.quote ?? '';
    const legacyHead = record.quoteHead ?? '';
    const folded = legacyHead === '' ? storedQuote : storedQuote === '' ? legacyHead : `${legacyHead}\n${storedQuote}`;
    const quote = folded === '' ? '' : reverseCitationOrder(normalizeQuoteMarkers(folded));

    const canonical =
        record.id !== undefined &&
        record.parentId !== undefined &&
        record.author !== undefined &&
        record.createdAt !== undefined &&
        record.updatedBy !== undefined &&
        record.updatedAt !== undefined &&
        record.text !== undefined &&
        // `''` counts as canonical: older clients wrote it on every record, and
        // treating it as legacy would hand back a fresh object per record on
        // every hydrate — which the flush's reference diff reads as "the whole
        // store changed" (a write storm on open).
        (record.quoteHead === undefined || record.quoteHead === '') &&
        record.quote === quote;
    // The cast is the check above, restated — TS can't narrow the fields of
    // one object at once. A leftover empty `quoteHead` rides along inert; the
    // wire writer never reads it.
    if (canonical) return record as NoteRecord;
    return {
        id: record.id ?? '',
        parentId: record.parentId ?? null,
        author: record.author ?? null,
        authorId: record.authorId ?? null,
        createdAt: record.createdAt ?? null,
        updatedBy: record.updatedBy ?? null,
        updatedById: record.updatedById ?? null,
        updatedAt: record.updatedAt ?? null,
        quote,
        text: record.text ?? '',
    };
}

/** Key records by their uuid — the shape every marker→record join takes. Notes
 *  with no id yet (unadopted legacy ones) have no join key and are skipped. */
export function indexNoteRecords(records: Iterable<NoteRecord>): Map<string, NoteRecord> {
    const byId = new Map<string, NoteRecord>();
    for (const record of records) {
        if (record.id !== '') byId.set(record.id, record);
    }
    return byId;
}

/** True when `raw` is exactly one bare placement marker and nothing else. */
export function isBareMarker(raw: string): boolean {
    return !raw.includes('\n') && parseBareMarkerId(raw) !== null;
}

/**
 * The marker text a parsed NOTE block came from — the only thing about the
 * block that describes the WHOLE note. Its `text` is the body alone: empty for
 * a bare marker, and stripped of the `>` quote lines even for a legacy one. So
 * every consumer that re-serialises or re-resolves a note starts here.
 *
 * Structurally typed rather than taking a `CompositeSimpleBlock`, to keep this
 * module free of the parser's types (both its callers are serialisers that
 * already hold one).
 */
export function noteMarkerSource(block: { raw?: string; text: string }): string {
    return block.raw ?? `${OPEN}${block.text}${CLOSE}`;
}

/**
 * Normalize every non-bare note marker in a scene's content to the bare wire
 * form, extracting the records they carried: a legacy `[[free text]]` gets a
 * minted id and becomes an authorless record; a transitional full-payload
 * marker keeps its id and yields everything it carried. Bare markers are left
 * untouched. Returns `null` when nothing changed, so callers can skip the
 * write.
 *
 * Runs at ingestion (hydrate/sync) only — never on live edits, where it would
 * rewrite text under the user's caret. A `[[text]]` note typed by hand
 * mid-session therefore stays legacy (rendered from an ephemeral parse-time
 * record) until the scene is next ingested or the note is next edited.
 *
 * The returned `shifts` describe the line-count change so callers can move the
 * scene's POSITIONAL persisted data (`paragraph_lines`, `draft_line_ids`)
 * along with it — see {@link LineIndexShift}.
 */
export function normalizeNoteMarkersInContent(content: string, mintId: () => string): NoteNormalization | null {
    const lines = content.split('\n');
    const spans = findNoteSpans(lines);
    if (spans.length === 0) return null;

    // Mint in document order (ids read in the order the notes appear), then
    // splice back-to-front so an earlier span's indices stay valid while a
    // later one is replaced with a different number of lines.
    const normalizations: { span: NoteSpan; record: NoteRecord }[] = [];
    for (const span of spans) {
        if (span.start === span.end && parseBareMarkerId(lines[span.start] ?? '') !== null) continue;
        const parsed = parseNoteMarker(lines.slice(span.start, span.end + 1).join('\n'));
        if (parsed === null) continue;
        normalizations.push({ span, record: parsed.id === '' ? { ...parsed, id: mintId() } : parsed });
    }
    if (normalizations.length === 0) return null;

    const shifts: LineIndexShift[] = [];
    for (let i = normalizations.length - 1; i >= 0; i--) {
        const normalization = normalizations[i];
        if (!normalization) continue;
        const was = normalization.span.end - normalization.span.start + 1;
        // Anchored one line past the marker's OPENING line: the opener keeps
        // its position, everything below it moves. (A tag pointing INSIDE a
        // multi-line marker lands on the marker either way.)
        if (was !== 1) shifts.push({ fromIndex: normalization.span.start + 1, delta: 1 - was });
        lines.splice(normalization.span.start, was, bareNoteMarker(normalization.record.id));
    }
    return { content: lines.join('\n'), shifts: shifts.reverse(), records: normalizations.map(n => n.record) };
}

/**
 * Map a line index recorded before normalization onto its position after it.
 * Order-independent: every shift anchored at or below `index` applies.
 */
export function shiftAdoptedLineIndex(shifts: readonly LineIndexShift[] | undefined, index: number): number {
    if (shifts === undefined || shifts.length === 0) return index;
    let shifted = index;
    for (const shift of shifts) {
        if (shift.fromIndex <= index) shifted += shift.delta;
    }
    return shifted;
}

function emptyNote(): NoteRecord {
    return { id: '', parentId: null, author: null, createdAt: null, updatedBy: null, updatedAt: null, quote: '', text: '' };
}

/**
 * Split the header's `key:value` run. Values may contain spaces (user labels
 * do), so a value runs up to the next known key rather than to the next space.
 */
function parseHeaderFields(header: string): Partial<Record<(typeof HEADER_KEYS)[number], string>> {
    const fields: Partial<Record<(typeof HEADER_KEYS)[number], string>> = {};
    const starts: { key: (typeof HEADER_KEYS)[number]; from: number; valueAt: number }[] = [];
    HEADER_KEY_RE.lastIndex = 0;
    let match = HEADER_KEY_RE.exec(header);
    while (match !== null) {
        const key = match[1] as (typeof HEADER_KEYS)[number];
        starts.push({ key, from: match.index, valueAt: match.index + match[0].length });
        match = HEADER_KEY_RE.exec(header);
    }
    for (let i = 0; i < starts.length; i++) {
        const cur = starts[i];
        if (!cur) continue;
        const end = starts[i + 1]?.from ?? header.length;
        fields[cur.key] = header.slice(cur.valueAt, end).trim();
    }
    return fields;
}

/** `<user>@<iso>` — the ISO stamp never contains `@`, so the LAST one splits
 *  the pair even when the label is an email address. */
function splitStamp(value: string | undefined): { user: string | null; iso: string | null } {
    if (value === undefined) return { user: null, iso: null };
    const at = value.lastIndexOf('@');
    if (at < 0) return { user: value === '' ? null : value, iso: null };
    return { user: value.slice(0, at), iso: value.slice(at + 1) || null };
}
